import { useMemo } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useCurrentUser from '@hooks/useCurrentUser'
import useCampaignQuoteTemplates from '@hooks/useCampaignQuoteTemplates'

import type { CampaignQuoteTemplateModel, CampaignQuoteTemplateRequestOptions } from '@models/types'

const watchEntityKeys = ['campaignQuoteTemplates']

type GetFilteredTemplatesParams = {
  currentUserQuoteTemplates: CampaignQuoteTemplateModel[],
  filterKey: string,
  filteredCampaignQuoteTemplates: CampaignQuoteTemplateModel[],
  isAgencyAdmin: boolean,
  unassignedQuoteTemplates: CampaignQuoteTemplateModel[],
}

const getFilteredUserTemplates = (params: GetFilteredTemplatesParams) => {
  const {
    currentUserQuoteTemplates,
    filterKey,
    filteredCampaignQuoteTemplates,
    isAgencyAdmin,
    unassignedQuoteTemplates,
  } = params

  if (filterKey === 'agency' || (filterKey === 'all' && !isAgencyAdmin)){
    return unassignedQuoteTemplates
  }

  if (filterKey === 'user') return currentUserQuoteTemplates

  return filteredCampaignQuoteTemplates
}

type CampaignQuoteTemplateFilters = {
  builder_version?: 1 | 2,
  hidden?: 'true' | 'false',
  string?: string,
  user_ids?: number[],
}

type UseCampaignQuoteTemplatesOptions = {
  filters?: CampaignQuoteTemplateFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteTemplateRequestOptions,
}

function useUserCampaignQuoteTemplates(options: UseCampaignQuoteTemplatesOptions) {
  const { filters = {}, performHttpRequests, requestOptions } = options
  const {
    updatedEntities: {
      campaignQuoteTemplates: campaignQuoteTemplatesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const campaignQuoteTemplatesPayload = useCampaignQuoteTemplates({
    filters,
    requestOptions,
    performHttpRequests,
  })

  const { filteredCampaignQuoteTemplates, loading } = campaignQuoteTemplatesPayload

  const { currentUser } = useCurrentUser()

  const currentUserQuoteTemplates = useMemo(() => {
    const filtered = filteredCampaignQuoteTemplates.filter((quoteTemplate) => {
      const userIds = digObject(quoteTemplate, 'data.user_ids', [])

      return !!userIds.length && userIds.includes(currentUser.id)
    })

    return filtered
  }, [campaignQuoteTemplatesUpdatedAt, currentUser.id])

  const unassignedQuoteTemplates = useMemo(() => {
    const filtered = filteredCampaignQuoteTemplates.filter((quoteTemplate) => {
      const userIds = digObject(quoteTemplate, 'data.user_ids', [])

      return !userIds.length
    })

    return filtered
  }, [campaignQuoteTemplatesUpdatedAt])

  return {
    callbacks: {
      getFilteredUserTemplates: (filterKey: string, isAgencyAdmin: boolean) => getFilteredUserTemplates({
        currentUserQuoteTemplates,
        filterKey,
        filteredCampaignQuoteTemplates,
        isAgencyAdmin,
        unassignedQuoteTemplates,
      }),
    },
    currentUserQuoteTemplates,
    filteredCampaignQuoteTemplates,
    loading,
    unassignedQuoteTemplates,
  }
}

export default useUserCampaignQuoteTemplates
